import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail, uploadImage, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PenLine, ImagePlus, X } from "lucide-react";

export default function CreatePost({ onCreated }) {
  const { meta } = useAuth();
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const path = await uploadImage(f);
      setImagePath(path);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!content.trim()) { toast.error("Please write something."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/posts", {
        content, specialty: specialty || null, image_url: imagePath || null,
      });
      toast.success("Posted");
      setContent(""); setSpecialty(""); setImagePath(""); setOpen(false);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="create-post-trigger-button"
          className="w-full justify-start rounded-full py-6 bg-white border border-[color:var(--ayur-border)] text-[color:var(--ayur-mute)] hover:bg-[color:var(--ayur-cream-2)] hover:text-[color:var(--ayur-ink)] shadow-sm"
          variant="ghost"
        >
          <PenLine className="w-4 h-4 mr-2" /> Share a thought, case, or study…
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="create-post-modal" className="bg-[color:var(--ayur-cream)] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Compose Post</DialogTitle>
          <DialogDescription className="text-[color:var(--ayur-mute)]">
            Share a clinical case, a Samhita insight, or a research question with the community.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            data-testid="create-post-text-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share a clinical case, a Samhita insight, a research question…"
            rows={6}
            className="bg-white resize-none"
          />

          {imagePath && (
            <div className="relative">
              <img src={resolveImageUrl(imagePath)} alt="preview" className="rounded-lg border border-[color:var(--ayur-border)] max-h-72 w-full object-cover" />
              <button onClick={() => setImagePath("")} className="absolute top-2 right-2 bg-white/90 rounded-full p-1 shadow" data-testid="post-image-remove">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="post-image-input" />
            <Button variant="outline" size="sm" onClick={onPick} disabled={uploading}
              data-testid="post-image-upload-button"
              className="border-[color:var(--ayur-border)] text-[color:var(--ayur-emerald)]">
              <ImagePlus className="w-4 h-4 mr-2" />
              {uploading ? "Uploading…" : imagePath ? "Change image" : "Add image"}
            </Button>
            <div className="flex-1" />
            <Select value={specialty} onValueChange={setSpecialty}>
              <SelectTrigger data-testid="create-post-specialty-select" className="bg-white w-56">
                <SelectValue placeholder="Tag a specialty" />
              </SelectTrigger>
              <SelectContent>
                {meta.specialties.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid="create-post-submit-button"
              onClick={submit} disabled={loading || uploading}
              className="bg-[color:var(--ayur-terracotta)] hover:bg-[color:var(--ayur-terracotta)]/90 text-white"
            >
              {loading ? "Posting…" : "Publish"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
